import React, { createContext, useCallback, useContext, useState } from "react";
import { cloneOfferDraft, createInitialOfferDraft } from "../utils/offerDraft";

const OfferContext = createContext(undefined);

export const OfferProvider = ({ children }) => {
	const [offers, setOffers] = useState([]);
	const [offersFilterKey, setOffersFilterKey] = useState("");
	const [offerGroups, setOfferGroups] = useState([]);
	const [editingOffer, setEditingOffer] = useState(null);
	const [editingOfferBaseline, setEditingOfferBaseline] = useState(null);
	const [createOfferDraft, setCreateOfferDraft] = useState(createInitialOfferDraft());
	const [currentPage, setCurrentPage] = useState(1);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [lastClickedOfferId, setLastClickedOfferIdState] = useState(null);

	const setOffersList = useCallback((items, page, hasNext, filterKey = "") => {
		setOffers(items);
		setCurrentPage(page);
		setHasNextPage(hasNext);
		setOffersFilterKey(filterKey);
	}, []);

	const appendOffersList = useCallback((items, page, hasNext, filterKey = "") => {
		setOffers((prev) => [...prev, ...items]);
		setCurrentPage(page);
		setHasNextPage(hasNext);
		setOffersFilterKey(filterKey);
	}, []);

	const setLastClickedOfferId = useCallback((id) => {
		setLastClickedOfferIdState(id);
	}, []);

	const clearOffersList = useCallback(() => {
		setOffersList([], 1, false, "");
		setLastClickedOfferIdState(null);
	}, [setOffersList]);

	const setOfferGroupsList = useCallback((groups) => {
		setOfferGroups(groups);
	}, []);

	const setEditingOfferDraft = useCallback((offer) => {
		setEditingOffer(offer);
		setEditingOfferBaseline(cloneOfferDraft(offer));
	}, []);

	const updateEditingOfferDraft = useCallback((updates) => {
		setEditingOffer((prev) => (prev ? { ...prev, ...updates } : prev));
	}, []);

	const clearEditingOffer = useCallback(() => {
		setEditingOffer(null);
		setEditingOfferBaseline(null);
	}, []);

	const updateCreateOfferDraft = useCallback((updates) => {
		setCreateOfferDraft((prev) => ({ ...prev, ...updates }));
	}, []);

	const resetCreateOfferDraft = useCallback(() => {
		setCreateOfferDraft(createInitialOfferDraft());
	}, []);

	const updateOfferStatusInList = useCallback((id, status) => {
		setOffers((prev) =>
			prev.map((item) =>
				String(item.id) === String(id) ? { ...item, status: String(status) } : item
			)
		);
	}, []);

	return (
		<OfferContext.Provider
			value={{
				offers,
				offersFilterKey,
				offerGroups,
				editingOffer,
				editingOfferBaseline,
				createOfferDraft,
				currentPage,
				hasNextPage,
				lastClickedOfferId,
				setOffersList,
				appendOffersList,
				clearOffersList,
				setLastClickedOfferId,
				setOfferGroupsList,
				setEditingOfferDraft,
				updateEditingOfferDraft,
				clearEditingOffer,
				updateCreateOfferDraft,
				resetCreateOfferDraft,
				updateOfferStatusInList,
			}}>
			{children}
		</OfferContext.Provider>
	);
};

export const useOfferContext = () => {
	const context = useContext(OfferContext);
	if (context === undefined) {
		throw new Error("useOfferContext must be used within an OfferProvider");
	}
	return context;
};
